/**
 * Environment configuration for the Patient Dashboard application.
 * This file provides a centralized way to access environment variables.
 */

/**
 * Helper function to get environment variable value
 * Works in both Vite and Jest environments.
 *
 * NOTE:
 * Do NOT reference `import.meta` directly in source code here. Jest runs tests in
 * a CommonJS context where `import.meta` is a syntax error. In Vite builds, our
 * `vite.config.ts` maps `process.env` to `import.meta.env`, so using `process.env`
 * is safe and keeps tests compatible.
 */
export function getEnvValue(envVar: string): string | undefined {
  // In Vite, `process.env` is replaced with `import.meta.env` at build time
  // via the `define: { "process.env": "import.meta.env" }` in vite.config.ts.
  // In Jest/Node, process.env is the real Node environment object.
  //
  // NOTE: Do NOT guard with `typeof process !== "undefined"`. Vite only replaces
  // the token `process.env` — not `process` itself — so the typeof guard always
  // evaluates to false in the browser, returning undefined for every flag.
  const value = (process.env as Record<string, unknown>)[envVar];
  return typeof value === "undefined" ? undefined : String(value);
}

/**
 * Build/runtime mode ("development" | "production" | …) without assuming `process.env` exists.
 * Some hosts or polyfills define `globalThis.process` as `{}` without `env`; patterns like
 * `typeof process !== "undefined" ? process.env.MODE` then throw. Prefer this helper.
 */
export function getRuntimeMode(): string {
  return getEnvValue("MODE") || getEnvValue("NODE_ENV") || "development";
}

export const env = {
  // API Configuration
  API_BASE_URL:
    getEnvValue("VITE_API_BASE_URL") || "http://localhost:8000/api/v1",
  WS_URL: getEnvValue("VITE_WS_URL") || "ws://localhost:8000/ws",

  // Environment
  MODE: getEnvValue("MODE") || "development",
  DEV:
    getEnvValue("DEV") === "true" ||
    (typeof process !== "undefined" && process.env?.NODE_ENV !== "production"),
  PROD:
    getEnvValue("PROD") === "true" ||
    (typeof process !== "undefined" && process.env?.NODE_ENV === "production"),

  // Debug Configuration
  DEBUG: getEnvValue("VITE_DEBUG") === "true" || getEnvValue("DEV") === "true",
  LOG_LEVEL: getEnvValue("VITE_LOG_LEVEL") || "info",

  // Feature Flags
  ENABLE_ANALYTICS: getEnvValue("VITE_ENABLE_ANALYTICS") === "true",
  ENABLE_ERROR_REPORTING: getEnvValue("VITE_ENABLE_ERROR_REPORTING") === "true",
  ENABLE_PERFORMANCE_MONITORING:
    getEnvValue("VITE_ENABLE_PERFORMANCE_MONITORING") === "true",
  // Opt-in: VITE_ENABLE_PROVIDER_FACILITY_PHOTOS=true must match backend ENABLE_PROVIDER_FACILITY_PHOTOS.
  ENABLE_PROVIDER_FACILITY_PHOTOS:
    getEnvValue("VITE_ENABLE_PROVIDER_FACILITY_PHOTOS") === "true",
  // Agency features are generally available. Opt out only with an explicit "false".
  ENABLE_CARE_COORDINATION_AGENCY_SIGNUP:
    getEnvValue("VITE_ENABLE_CARE_COORDINATION_AGENCY_SIGNUP") !== "false",
  ENABLE_CARE_COORDINATION_AGENCY_MEMBER_MANAGEMENT:
    getEnvValue("VITE_ENABLE_CARE_COORDINATION_AGENCY_MEMBER_MANAGEMENT") !==
    "false",
  ENABLE_CARE_COORDINATION_AGENCY_INVITATIONS:
    getEnvValue("VITE_ENABLE_CARE_COORDINATION_AGENCY_INVITATIONS") !== "false",
  /** Opt-in: fully deprecate Search Analytics widget (Phase 4 — after enable-rate review). */
  DEPRECATE_SEARCH_ANALYTICS_WIDGET:
    getEnvValue("VITE_DEPRECATE_SEARCH_ANALYTICS_WIDGET") === "true",
  /**
   * Plan 29 P2.3: muted “Typical for type” tooltip on search card/map chips when
   * only type-default services are shown. Off by default.
   */
  ENABLE_PROVIDER_CARD_TYPE_DEFAULT_HINT:
    getEnvValue("VITE_ENABLE_PROVIDER_CARD_TYPE_DEFAULT_HINT") === "true",
} as const;

/** Phase 4 facility photos — single frontend gate (see STAGING_OPERATIONS.md). */
export const isProviderFacilityPhotosEnabled = (): boolean =>
  getEnvValue("VITE_ENABLE_PROVIDER_FACILITY_PHOTOS") === "true";

/** Plan 29 P2.3 — “Typical for type” chip hint (off unless env is true). */
export const isProviderCardTypeDefaultHintEnabled = (): boolean =>
  getEnvValue("VITE_ENABLE_PROVIDER_CARD_TYPE_DEFAULT_HINT") === "true";

// Type-safe environment variable access
export const isDevelopment = env.MODE === "development";
export const isProduction = env.MODE === "production";
export const isDebugMode = env.DEBUG;

/**
 * Known frontend origins per environment (for origin-based API resolution).
 * When the app is loaded from one of these hosts, we use the corresponding API
 * even if build-time env was wrong (defense in depth).
 */
const STAGING_FRONTEND_ORIGINS = [
  "https://staging.nexagridhealth.com",
  "https://jolly-glacier-00960b710.2.azurestaticapps.net",
];
const PRODUCTION_FRONTEND_ORIGINS = [
  "https://www.nexagridhealth.com",
  "https://nexagridhealth.com",
];

const STAGING_API_BASE = "https://api-staging.nexagridhealth.com/api/v1";
const PRODUCTION_API_BASE = "https://api.nexagridhealth.com/api/v1";

/**
 * Resolves the API base URL at runtime (industry practice: origin-based config).
 * If the app is served from a known staging/production host, use that environment's API;
 * otherwise use build-time env. In Vite dev (any host — localhost, LAN IP, Docker bridge),
 * use relative `/api/v1` so the dev-server proxy avoids cross-origin calls to localhost:8000.
 */
function resolveApiBaseUrl(): string {
  if (getRuntimeMode() === "test") {
    return env.API_BASE_URL;
  }
  if (typeof window !== "undefined" && window.location?.origin) {
    const origin = window.location.origin;
    if (STAGING_FRONTEND_ORIGINS.includes(origin)) return STAGING_API_BASE;
    if (PRODUCTION_FRONTEND_ORIGINS.includes(origin)) return PRODUCTION_API_BASE;
    if (getRuntimeMode() === "development") {
      return "/api/v1";
    }
  }
  return env.API_BASE_URL;
}

// Helper functions
export const getApiUrl = (endpoint: string = ""): string => {
  const base = resolveApiBaseUrl();
  // Production/staging: fail-fast if resolved URL is localhost (wrong build deployed)
  if (
    env.PROD &&
    (base.includes("localhost") || base.startsWith("http://127."))
  ) {
    throw new Error(
      "API_BASE_URL must be set at build time for production/staging. " +
        "Redeploy using the staging or production build (e.g. build-staging.ps1 or deploy-frontend.ps1 -Environment staging)."
    );
  }
  return `${base}${endpoint}`;
};

/** API origin only (e.g. https://api.nexagridhealth.com) for health checks and non-/api/v1 paths */
export const getApiOrigin = (): string => {
  const base = resolveApiBaseUrl();
  if (base.startsWith("/")) {
    // Vite dev proxy — use same-origin relative paths (works on localhost and LAN IPs).
    return "";
  }
  if (
    env.PROD &&
    (base.includes("localhost") || base.startsWith("http://127."))
  ) {
    throw new Error(
      "API_BASE_URL must be set at build time for production/staging. " +
        "Redeploy using the staging or production build script."
    );
  }
  try {
    return new URL(base).origin;
  } catch {
    if (env.PROD) {
      throw new Error(
        `Invalid API_BASE_URL configuration: ${base}. ` +
          `Ensure VITE_API_BASE_URL is properly set during build.`
      );
    }
    return "http://localhost:8000";
  }
};

export const getWebSocketUrl = (path: string = "") => {
  const configured = getEnvValue("VITE_WS_URL");
  if (configured) return `${configured.replace(/\/$/, "")}${path}`;
  if (typeof window !== "undefined" && window.location?.host) {
    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    return `${protocol}//${window.location.host}/ws${path}`;
  }
  return `${env.WS_URL}${path}`;
};

/**
 * Resolve API paths for raw `fetch()` calls.
 * - Local Vite dev (any host — localhost, 127.0.0.1, or LAN IP like 192.168.x.x): keep relative
 *   `/api/...` so the dev-server proxy forwards to the backend (same-origin; cookies work).
 * - Staging/production SWA hosts: prefix with API origin (no /api proxy on static hosting).
 */
export function resolveFetchApiUrl(path: string): string {
  if (path.startsWith("http://") || path.startsWith("https://")) {
    return path;
  }
  const normalized =
    path === "" ? "" : path.startsWith("/") ? path : `/${path}`;
  const base = resolveApiClientBaseUrl();
  return base ? `${base}${normalized}` : normalized;
}

/**
 * Axios base URL: empty string in Vite dev (proxy), API origin on deployed frontends.
 */
export function resolveApiClientBaseUrl(): string {
  if (getRuntimeMode() === "test") {
    return getApiOrigin();
  }
  if (typeof window !== "undefined" && window.location?.origin) {
    const origin = window.location.origin;
    if (
      STAGING_FRONTEND_ORIGINS.includes(origin) ||
      PRODUCTION_FRONTEND_ORIGINS.includes(origin)
    ) {
      return getApiOrigin();
    }
    if (getRuntimeMode() === "development") {
      return "";
    }
  }
  return getApiOrigin();
}

/**
 * Canonical public frontend origin for shareable URLs (profile copy link, OG tags).
 * Prefers VITE_PUBLIC_APP_URL when set (e.g. custom domain behind a proxy);
 * otherwise uses the current browser origin so staging/dev copies match what users share from.
 */
export function resolvePublicAppOrigin(): string {
  const configured = getEnvValue("VITE_PUBLIC_APP_URL")?.trim();
  if (configured) {
    return configured.replace(/\/$/, "");
  }
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin;
  }
  return "";
}

/**
 * Enhanced Logging Service
 * Provides structured logging with different levels and contexts
 */

export type LogLevel = "debug" | "info" | "warn" | "error";
export type LogContext =
  | "API"
  | "AUTH"
  | "UI"
  | "NETWORK"
  | "STORAGE"
  | "SECURITY"
  | "PERFORMANCE"
  | "ProviderAPI"
  | "PublicAPI"
  | "RecentSearch"
  | "PatientDashboard"
  | "SocialWorkerDashboard"
  | "useProviderEdit"
  | "SavedSearchesPage"
  | "RecommendationsAPI"
  | "useRoleDashboard";

export interface LogEntry {
  level: LogLevel;
  message: string;
  context?: LogContext;
  timestamp: string;
  data?: unknown;
  stack?: string;
}

class Logger {
  private isDebugMode = env.DEV || env.DEBUG;
  private logHistory: LogEntry[] = [];
  private maxHistorySize = 1000;

  /**
   * Log a message with structured format
   */
  log(
    level: LogLevel,
    message: string,
    context?: LogContext,
    data?: unknown
  ): void {
    const entry: LogEntry = {
      level,
      message,
      context,
      timestamp: new Date().toISOString(),
      // The history can be exported from the browser, so sanitize before it
      // is retained rather than only at the console/external logger boundary.
      data: this.sanitizeLogData(data),
    };

    // Add stack trace for errors
    if (level === "error") {
      entry.stack = new Error().stack;
    }

    // Store in history
    this.logHistory.push(entry);
    if (this.logHistory.length > this.maxHistorySize) {
      this.logHistory.shift();
    }

    // Output to console based on level and debug mode
    // SECURITY: Only log in development or for errors/warnings in production
    if (this.shouldLog(level)) {
      const formattedMessage = this.formatMessage(entry);
      // Sanitize data before logging (never log sensitive information)
      console[level](formattedMessage, entry.data || "");
    }

    // Send to external logging service in production
    if (!this.isDebugMode && level === "error") {
      this.sendToExternalLogger(entry);
    }
  }

  /**
   * Debug logging (only in development)
   */
  debug(message: string, context?: LogContext, data?: unknown): void {
    this.log("debug", message, context, data);
  }

  /**
   * Info logging
   */
  info(message: string, context?: LogContext, data?: unknown): void {
    this.log("info", message, context, data);
  }

  /**
   * Warning logging
   */
  warn(message: string, context?: LogContext, data?: unknown): void {
    this.log("warn", message, context, data);
  }

  /**
   * Error logging
   */
  error(message: string, context?: LogContext, data?: unknown): void {
    this.log("error", message, context, data);
  }

  /**
   * Get log history for debugging
   */
  getHistory(level?: LogLevel, context?: LogContext): LogEntry[] {
    return this.logHistory.filter((entry) => {
      if (level && entry.level !== level) return false;
      if (context && entry.context !== context) return false;
      return true;
    });
  }

  /**
   * Clear log history
   */
  clearHistory(): void {
    this.logHistory = [];
  }

  /**
   * Export logs for debugging
   */
  exportLogs(): string {
    return JSON.stringify(this.logHistory, null, 2);
  }

  /**
   * Check if message should be logged
   * Production-safe: Only errors and warnings in production, all levels in development
   */
  private shouldLog(level: LogLevel): boolean {
    if (this.isDebugMode) return true;
    // In production, only log errors and warnings (never debug/info)
    return level === "error" || level === "warn";
  }

  /**
   * Format log message with context and timestamp
   */
  private formatMessage(entry: LogEntry): string {
    const contextStr = entry.context ? `[${entry.context}]` : "";
    const timeStr = new Date(entry.timestamp).toLocaleTimeString();
    return `${timeStr} ${contextStr} ${entry.message}`;
  }

  /**
   * Sanitize log data to prevent sensitive information leakage
   */
  private sanitizeLogData(data: unknown): unknown {
    if (!data) return data;

    if (typeof data === "string") {
      // Check for sensitive patterns
      const sensitivePatterns = [
        /token/i,
        /password/i,
        /secret/i,
        /key/i,
        /auth/i,
        /credential/i,
        /bearer\s+[\w-]+/i, // Bearer tokens
      ];

      for (const pattern of sensitivePatterns) {
        if (pattern.test(data)) {
          return "[REDACTED - Contains sensitive data]";
        }
      }

      // Truncate very long strings
      if (data.length > 500) {
        return data.substring(0, 500) + "... [truncated]";
      }
    }

    if (typeof data === "object" && data !== null) {
      if (Array.isArray(data)) {
        return data.map((item) => this.sanitizeLogData(item));
      }
      const sanitized: Record<string, unknown> = {};
      const sensitiveKeys = [
        "token",
        "password",
        "secret",
        "key",
        "auth",
        "credential",
        "authorization",
      ];

      for (const [key, value] of Object.entries(data)) {
        const lowerKey = key.toLowerCase();
        if (sensitiveKeys.some((sensitive) => lowerKey.includes(sensitive))) {
          sanitized[key] = "[REDACTED]";
        } else {
          sanitized[key] = this.sanitizeLogData(value);
        }
      }
      return sanitized;
    }

    return data;
  }

  /**
   * Send error logs to external service
   * SECURITY: Only sends sanitized error logs, never sensitive data
   */
  private async sendToExternalLogger(entry: LogEntry): Promise<void> {
    try {
      // Sanitize entry before sending
      const sanitizedEntry: LogEntry = {
        ...entry,
        data: this.sanitizeLogData(entry.data),
        // Remove stack traces in production (may contain sensitive paths)
        stack: this.isDebugMode ? entry.stack : undefined,
      };

      // In a real implementation, this would send to a logging service
      // like Sentry, LogRocket, or a custom logging endpoint
      const logsUrl = `${getApiOrigin()}/api/logs`;
      await fetch(logsUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // Include cookies when authenticated so backend can correlate user_id.
        credentials: "include",
        body: JSON.stringify({
          level: sanitizedEntry.level,
          message: sanitizedEntry.message,
          context: sanitizedEntry.context,
          // Never send stack to server — backend drops it; omit client-side too.
          timestamp: sanitizedEntry.timestamp,
        }),
      });
    } catch (error) {
      // Only log to console in development if external logging fails
      if (this.isDebugMode) {
        console.error("Failed to send log to external service:", error);
      }
    }
  }
}

// Create singleton instance
const logger = new Logger();

// Export convenience functions
export const log = (
  level: LogLevel,
  message: string,
  ...args: unknown[]
): void => {
  // Single payload stays an object (not `[payload]` → Chrome `{0:…}`).
  const data =
    args.length === 0 ? undefined : args.length === 1 ? args[0] : args;
  logger.log(level, message, undefined, data);
};

export const logDebug = (
  message: string,
  context?: LogContext,
  data?: unknown
): void => {
  logger.debug(message, context, data);
};

export const logInfo = (
  message: string,
  context?: LogContext,
  data?: unknown
): void => {
  logger.info(message, context, data);
};

export const logWarn = (
  message: string,
  context?: LogContext,
  data?: unknown
): void => {
  logger.warn(message, context, data);
};

export const logError = (
  message: string,
  context?: LogContext,
  data?: unknown
): void => {
  logger.error(message, context, data);
};

export const getLogHistory = (
  level?: LogLevel,
  context?: LogContext
): LogEntry[] => {
  return logger.getHistory(level, context);
};

export const clearLogHistory = (): void => {
  logger.clearHistory();
};

export const exportLogs = (): string => {
  return logger.exportLogs();
};

export default env;
